<template>
  <div id="CampCheckout">
    <div class="headerbg">
      <b-container>
        <h1>營隊報名</h1>
      </b-container>
    </div>
    <b-container>
      <!-- 主梯次不可報名：保留購物車項目並阻擋送出，需返回購物車調整 -->
      <b-row v-if="checkout.BlockedItems && checkout.BlockedItems.length" class="mb-3">
        <b-col cols="12">
          <b-alert show variant="danger">
            <div class="font-weight-bold mb-1">
              以下營隊梯次已無法報名，請返回購物車調整後再結帳：
            </div>
            <ul class="mb-0 pl-3">
              <li
                v-for="(blocked, idx) in checkout.BlockedItems"
                :key="'blocked-' + idx"
              >
                {{ blocked.RelationName }}－{{ blocked.CampProductTitle }}：{{
                  blocked.Reason
                }}
              </li>
            </ul>
          </b-alert>
        </b-col>
      </b-row>

      <!-- 加購剪除提示：本次 checkout 範圍內的加購已被移除並重新計算金額 -->
      <b-row v-if="checkout.RemovedAddons && checkout.RemovedAddons.length" class="mb-3">
        <b-col cols="12">
          <b-alert show variant="warning">
            <div class="font-weight-bold mb-1">
              以下加購項目已從本次結帳移除，金額已重新計算：
            </div>
            <ul class="mb-0 pl-3">
              <li
                v-for="(removed, idx) in checkout.RemovedAddons"
                :key="'removed-' + idx"
              >
                {{ removed.CampProductName }}（原價 $
                {{ removed.OriginalPrice | money(0, 2) }}）：{{
                  removed.Reason
                }}
              </li>
            </ul>
          </b-alert>
        </b-col>
      </b-row>

      <!-- 購物車摘要 -->
      <b-row class="mb-3">
        <b-col cols="12">
          <b-card class="mb-3">
            <b-row class="d-flex flex-wrap p-0">
              <b-col cols="5" class="font-weight-bold text-nowrap"
                >營隊梯次</b-col
              >
              <b-col cols="3" class="font-weight-bold text-nowrap"
                >學員姓名</b-col
              >
              <b-col cols="2" class="font-weight-bold text-nowrap">日期</b-col>
              <b-col cols="2" class="font-weight-bold text-nowrap">金額</b-col>
              <b-col cols="12"><hr /></b-col>
            </b-row>
            <b-row
              v-for="(item, index) in checkout.CartDto"
              :key="item.Id"
              class="d-flex flex-wrap p-0 align-items-center"
            >
              <b-col cols="5">
                <div>
                  {{ item.CampProductTitle }}
                  <b-badge v-if="item.IsCampAddon" variant="info" class="ml-1"
                    >加購價</b-badge
                  >
                </div>
                <div class="small text-muted">{{ item.CampProductName }}</div>
              </b-col>
              <b-col cols="3">{{ item.RelationName }}</b-col>
              <b-col cols="2" class="small">
                {{ item.Start | datefmt("MM/DD") }} ~
                {{ item.End | datefmt("MM/DD") }}
              </b-col>
              <b-col cols="2">$ {{ item.Price | money(0, 2) }}</b-col>
              <b-col cols="12" v-if="index + 1 !== checkout.CartDto.length"
                ><hr
              /></b-col>
            </b-row>
            <b-col
              cols="12"
              style="height:2px;background:#000"
              class="my-3"
            ></b-col>
            <b-col cols="12" class="text-right camp-summary-panel">
              <div>商品小計：$ {{ checkout.ProductPrice | money(0, 2) }}</div>
              <div
                v-if="Number(checkout.Discount || 0) < 0"
                class="text-danger"
              >
                - $ {{ Math.abs(Number(checkout.Discount || 0)) | money(0, 2) }}
              </div>
              <div class="font-weight-bold">
                應付總額：$ {{ checkout.TotalPrice | money(0, 2) }}
              </div>
              <div
                v-if="
                  Number(checkout.Discount || 0) < 0 &&
                    checkoutDiscountItems.length
                "
                class="mt-1 camp-discount-list"
              >
                <div
                  class="camp-discount-heading"
                  :class="{ collapsed: discountsCollapsed }"
                  role="button"
                  tabindex="0"
                  :aria-expanded="(!discountsCollapsed).toString()"
                  @click="discountsCollapsed = !discountsCollapsed"
                  @keydown.enter.prevent="discountsCollapsed = !discountsCollapsed"
                  @keydown.space.prevent="discountsCollapsed = !discountsCollapsed"
                >
                  <span class="camp-discount-heading-icon" aria-hidden="true">%</span>
                  <span class="camp-discount-count">({{ checkoutDiscountItems.length }})</span>
                  已套用優惠
                </div>
                <span
                  v-for="(discount, discountIndex) in checkoutDiscountItems"
                  :key="'discount-' + discountIndex"
                  class="camp-discount-tag"
                  v-show="!discountsCollapsed"
                >
                  <span class="camp-discount-name">{{
                    discountName(discount)
                  }}</span>
                  <span class="camp-discount-amount"
                    >-{{ discountAmount(discount) }}元</span
                  >
                </span>
              </div>
            </b-col>
          </b-card>
        </b-col>
      </b-row>

      <b-row
        v-if="checkout.AvailableCoupons && checkout.AvailableCoupons.length"
        class="mb-3"
      >
        <b-col cols="12">
          <b-card title="使用折價券">
            <div class="camp-coupon-list mb-3">
              <b-button
                v-for="coupon in checkout.AvailableCoupons"
                :key="coupon.AssetCouponId"
                type="button"
                variant="outline-primary"
                class="camp-coupon-option"
                :title="couponOptionText(coupon)"
                :aria-label="couponOptionText(coupon)"
                :class="{ selected: isCouponSelected(coupon.AssetCouponId) }"
                :aria-pressed="isCouponSelected(coupon.AssetCouponId) ? 'true' : 'false'"
                :disabled="couponLoading"
                @click="toggleCoupon(coupon.AssetCouponId)"
              >
                <span class="camp-coupon-option-check" aria-hidden="true">{{
                  isCouponSelected(coupon.AssetCouponId) ? "✓" : "+"
                }}</span>
                <span class="camp-coupon-option-name">{{ coupon.CouponName || "折價券" }}</span>
                <span class="camp-coupon-option-amount">折抵 ${{ discountAmount(coupon) }}</span>
              </b-button>
            </div>
            <small class="text-muted d-block">
              可多選，再次點擊即可取消。
            </small>
          </b-card>
        </b-col>
      </b-row>

      <!-- 團報同行人屬於整張 CampOrder，只提供一個輸入框，按鈕確認後才呼叫 Preview 重新試算。 -->
      <b-row v-if="showGroupApplySection" class="mb-3">
        <b-col cols="12">
          <b-card title="團報資料">
            <b-form-group>
              <template #label>
                <span>團報同行人</span>
                <span
                  v-if="UploadedGroupApplyNames.length"
                  class="camp-group-tags ml-2"
                >
                  <span
                    v-for="name in UploadedGroupApplyNames"
                    :key="'group-name-' + name"
                    class="camp-group-tag"
                  >
                    {{ name }}
                  </span>
                </span>
              </template>
              <b-input-group>
                <b-form-input
                  v-model="checkout.GroupApplyName"
                  maxlength="500"
                  placeholder="例如：王小明,陳小華"
                  :state="getGroupFieldState()"
                  @input="
                    onGroupApplyInput()
                  "
                />
                <b-input-group-append>
                  <b-button variant="primary" @click="applyGroupApplyData">
                    上傳團報資料
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <b-form-text>
                若有多位團報者，請在每個姓名後以逗號分隔，例如：王小明,陳小華。以利管理員後續查詢團報資格。
              </b-form-text>
              <b-form-invalid-feedback
                v-if="submitAttempted && formErrors.GroupApplyName"
              >
                {{ formErrors.GroupApplyName }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>

      <!-- 學員資料：同一位學員可能有多筆報名（主梯次＋加購），只顯示一張摺疊卡片，避免重複 id 導致點一個全部展開 -->
      <template v-for="item in registrantItems">
        <b-card no-body class="mb-3" :key="'rel-' + item.RelationId">
          <b-card-header
            v-b-toggle="'rel-' + item.RelationId"
            class="d-flex justify-content-between align-items-center camp-collapse-header"
          >
            <div>
              <strong>{{ item.RelationName }}</strong>
              <span class="text-muted small ml-2">{{
                registrantSessionsLabel(item.RelationId)
              }}</span>
            </div>
            <span class="camp-chevron">▼</span>
          </b-card-header>
          <b-collapse
            :id="'rel-' + item.RelationId"
            accordion="camp-checkout-collapse"
          >
            <b-card-body>
              <div class="camp-form-section-title">基本資料</div>
              <b-alert
                v-if="submitAttempted && registrantError(item.RelationId)"
                show
                variant="danger"
              >
                {{ registrantError(item.RelationId) }}
              </b-alert>
              <b-row>
                <b-col cols="12">
                  <b-form-group label="姓名 *">
                    <b-form-input
                      v-model="item.Relation.Name"
                      placeholder="姓名"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-row>
                    <b-col sm="4">
                      <b-form-group label="英文姓氏">
                        <b-form-input
                          v-model="item.Relation.EnglishLastName"
                          placeholder="選填"
                          maxlength="255"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="4">
                      <b-form-group label="英文名字">
                        <b-form-input
                          v-model="item.Relation.EnglishFirstName"
                          placeholder="選填"
                          maxlength="255"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="4">
                      <b-form-group label="英文別名">
                        <b-form-input
                          v-model="item.Relation.EnglishName"
                          placeholder="選填，例如 Steven"
                          maxlength="255"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-text class="mb-3">姓與名請分開填寫，與護照相同</b-form-text>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="性別 *">
                    <b-form-radio-group v-model="item.Relation.Gender">
                      <b-form-radio :value="1">男</b-form-radio>
                      <b-form-radio :value="2">女</b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="身分證字號">
                    <b-form-input
                      v-model="item.Relation.PersonalId"
                      placeholder="身分證字號"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="出生日期 *">
                    <b-form-input
                      v-model="item.Relation.Birthday"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="就讀學校 *">
                    <b-form-input
                      v-model="item.Relation.School"
                      placeholder="就讀學校"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="年級 *">
                    <b-form-select
                      v-model="item.Relation.Grade"
                      :options="gradeOptions"
                    />
                    <b-form-text v-if="!gradeSummerNotice" class="text-muted">請依新學年度為主</b-form-text>
                    <b-form-text v-if="gradeSummerNotice" class="text-danger">{{ gradeSummerNotice }}</b-form-text>
                    <b-form-text
                      v-if="
                        isGradeBirthdayMismatch(
                          item.Relation.Grade,
                          item.Relation.Birthday,
                        )
                      "
                      class="text-warning"
                    >
                      {{ gradeBirthdayMismatchMessage }}
                    </b-form-text>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                  v-if="showGradeMemo(item.Relation.Grade)"
                >
                  <b-form-group label="年級備註 *">
                    <b-form-input
                      v-model="item.Relation.GradeMemo"
                      placeholder="請說明目前就學狀況"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="camp-form-section-title mt-2">緊急聯絡</div>
              <b-row>
                <b-col cols="12" md="4">
                  <b-form-group label="緊急聯絡人">
                    <b-form-input
                      v-model="item.Relation.EmergencyContact"
                      placeholder="姓名"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="關係">
                    <b-form-input
                      v-model="item.Relation.EmergencyRelation"
                      placeholder="例：父、母"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="緊急聯絡電話">
                    <b-form-input
                      v-model="item.Relation.EmergencyPhone"
                      placeholder="電話"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="camp-form-section-title mt-2">健康與飲食</div>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="飲食">
                    <b-form-radio-group v-model="item.Relation.IsVegetarian">
                      <b-form-radio :value="false">葷</b-form-radio>
                      <b-form-radio :value="true">素</b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="飲食備註">
                    <b-form-input
                      v-model="item.Relation.DietMemo"
                      placeholder="無則留空"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="過敏史">
                    <b-form-input
                      v-model="item.Relation.Allergy"
                      placeholder="無則留空"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="醫療備註">
                    <b-form-input
                      v-model="item.Relation.MedicalMemo"
                      placeholder="無則留空"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="備註">
                    <b-form-input
                      v-model="item.Relation.Memo"
                      placeholder="無則留空"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-checkbox v-model="item.SyncToLearnerProfile">
                    同步到家庭成員資料
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>
        </b-card>
      </template>

      <!-- 家長資訊 -->
      <b-row class="mb-3">
        <b-col cols="12">
          <b-card no-body class="mb-3">
            <b-card-header
              v-b-toggle="'member-info'"
              class="d-flex justify-content-between align-items-center camp-collapse-header"
            >
              <strong>家長資訊</strong>
              <span class="camp-chevron">▼</span>
            </b-card-header>
            <b-collapse id="member-info" accordion="camp-checkout-collapse">
              <b-card-body>
                <b-form>
                  <b-row>
                    <b-col>
                      <b-form-group label-for="last-name">
                        <template #label>
                          <span class="d-inline-flex align-items-center">
                            <b-badge variant="danger" class="mt-1 mr-2"
                              >必填</b-badge
                            >姓
                          </span>
                        </template>
                        <b-form-input
                          id="last-name"
                          v-model="checkout.MemberInfo.LastName"
                          placeholder="姓"
                          :state="getFieldState('LastName')"
                          @input="onFieldInput"
                        />
                        <b-form-invalid-feedback
                          v-if="submitAttempted && formErrors.LastName"
                        >
                          {{ formErrors.LastName }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </b-col>
                    <b-col>
                      <b-form-group label-for="first-name">
                        <template #label>
                          <span class="d-inline-flex align-items-center">
                            <b-badge variant="danger" class="mt-1 mr-2"
                              >必填</b-badge
                            >名
                          </span>
                        </template>
                        <b-form-input
                          id="first-name"
                          v-model="checkout.MemberInfo.FirstName"
                          placeholder="名"
                          :state="getFieldState('FirstName')"
                          @input="onFieldInput"
                        />
                        <b-form-invalid-feedback
                          v-if="submitAttempted && formErrors.FirstName"
                        >
                          {{ formErrors.FirstName }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group>
                    <template #label>
                      <span class="d-inline-flex align-items-center">
                        <b-badge variant="danger" class="mt-1 mr-2"
                          >必填</b-badge
                        >性別
                      </span>
                    </template>
                    <b-row>
                      <b-col>
                        <b-form-radio
                          v-model="checkout.MemberInfo.Gender"
                          name="gender"
                          :value="0"
                          :state="getFieldState('Gender')"
                          @change="onFieldInput"
                          >男</b-form-radio
                        >
                      </b-col>
                      <b-col>
                        <b-form-radio
                          v-model="checkout.MemberInfo.Gender"
                          name="gender"
                          :value="1"
                          :state="getFieldState('Gender')"
                          @change="onFieldInput"
                          >女</b-form-radio
                        >
                      </b-col>
                    </b-row>
                    <b-form-invalid-feedback
                      :state="getFieldState('Gender')"
                      v-if="submitAttempted && formErrors.Gender"
                    >
                      {{ formErrors.Gender }}
                    </b-form-invalid-feedback>
                  </b-form-group>

                  <b-form-group label-for="phone">
                    <template #label>
                      <span class="d-inline-flex align-items-center">
                        <b-badge variant="danger" class="mt-1 mr-2"
                          >必填</b-badge
                        >電話
                      </span>
                    </template>
                    <b-form-input
                      id="phone"
                      v-model="checkout.MemberInfo.Phone"
                      placeholder="電話"
                      :state="getFieldState('Phone')"
                      @input="onFieldInput"
                    />
                    <b-form-invalid-feedback
                      v-if="submitAttempted && formErrors.Phone"
                    >
                      {{ formErrors.Phone }}
                    </b-form-invalid-feedback>
                  </b-form-group>

                  <b-form-group label-for="email">
                    <template #label>
                      <span class="d-inline-flex align-items-center">
                        <b-badge variant="danger" class="mt-1 mr-2"
                          >必填</b-badge
                        >Email
                      </span>
                    </template>
                    <b-form-input
                      id="email"
                      v-model="checkout.MemberInfo.Email"
                      placeholder="Email"
                      :state="getFieldState('Email')"
                      @input="onFieldInput"
                    />
                    <b-form-invalid-feedback
                      v-if="submitAttempted && formErrors.Email"
                    >
                      {{ formErrors.Email }}
                    </b-form-invalid-feedback>
                  </b-form-group>

                  <b-form-group>
                    <template #label>
                      <span class="d-inline-flex align-items-center">
                        <b-badge variant="danger" class="mt-1 mr-2"
                          >必填</b-badge
                        >地址
                      </span>
                    </template>
                    <b-row class="mb-3">
                      <b-col>
                        <b-form-select
                          v-model="checkout.MemberInfo.City"
                          @change="onCityChange"
                          :state="getFieldState('City')"
                        >
                          <b-form-select-option value=""
                            >請選擇縣市</b-form-select-option
                          >
                          <b-form-select-option
                            v-for="c in cityList"
                            :key="c.Id"
                            :value="c.Name"
                          >
                            {{ c.Name }}
                          </b-form-select-option>
                        </b-form-select>
                        <b-form-invalid-feedback
                          v-if="submitAttempted && formErrors.City"
                        >
                          {{ formErrors.City }}
                        </b-form-invalid-feedback>
                      </b-col>
                      <b-col>
                        <b-form-input
                          v-model="checkout.MemberInfo.Town"
                          placeholder="行政區"
                          :state="getFieldState('Town')"
                          @input="onFieldInput"
                        />
                        <b-form-invalid-feedback
                          v-if="submitAttempted && formErrors.Town"
                        >
                          {{ formErrors.Town }}
                        </b-form-invalid-feedback>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col cols="12">
                        <b-form-input
                          v-model="checkout.MemberInfo.Address"
                          placeholder="地址"
                          :state="getFieldState('Address')"
                          @input="onFieldInput"
                        />
                        <b-form-invalid-feedback
                          v-if="submitAttempted && formErrors.Address"
                        >
                          {{ formErrors.Address }}
                        </b-form-invalid-feedback>
                      </b-col>
                    </b-row>
                  </b-form-group>
                </b-form>
              </b-card-body>
            </b-collapse>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-if="submitAttempted && hasRegistrantErrors">
        <b-col cols="12">
          <b-alert show variant="danger">
            學員資料尚未填寫完整，請展開下方對應學員確認：
            <ul class="mb-0 mt-1 pl-3">
              <li
                v-for="(message, relationId) in formErrors.Registrants"
                :key="relationId"
              >
                {{ registrantNameById(relationId) }}：{{ message }}
              </li>
            </ul>
          </b-alert>
        </b-col>
      </b-row>

      <!-- 收款方式：現金或轉帳，送出訂單後即固定不可變更 -->
      <b-row class="mb-3">
        <b-col cols="12">
          <b-card title="收款方式">
            <b-form-group class="mb-0">
              <b-form-radio-group
                v-model="paymentMethod"
                :options="paymentMethodOptions"
                stacked
                @change="onFieldInput"
              ></b-form-radio-group>
              <div
                v-if="submitAttempted && formErrors.PaymentMethod"
                class="text-danger small mt-1"
              >
                {{ formErrors.PaymentMethod }}
              </div>
              <small class="text-muted d-block mt-1">
                選擇「現金結帳」請於指定時間內至現場繳費；選擇「轉帳」請於下一頁回報匯款資訊。送出訂單後不可變更收款方式。
              </small>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" class="d-flex justify-content-end mb-5">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            @click="_go('/campCart')"
            >上一步</b-button
          >
          <b-button
            variant="primary"
            :disabled="submitting || hasBlockedItems"
            @click="openSubmitDialog"
            >提交訂單</b-button
          >
        </b-col>
      </b-row>

      <!-- 提交訂單：建立訂單前選擇稍後付款或立即結帳，兩者都建立同一筆訂單與付款單 -->
      <b-modal
        id="modal-camp-submit-order"
        title="提交訂單"
        centered
        no-close-on-backdrop
        :no-close-on-esc="submitting"
        :hide-header-close="submitting"
      >
        <div class="mb-2">應付總額：$ {{ checkout.TotalPrice | money(0, 2) }}</div>
        <div class="mb-3">收款方式：{{ paymentMethodText }}</div>
        <small class="text-muted d-block">
          提交後即建立訂單與付款單。選擇「立即結帳」前往付款頁；選擇「稍後付款」前往訂單明細，之後可隨時回報付款。
        </small>
        <template #modal-footer>
          <b-button
            variant="outline-secondary"
            :disabled="submitting"
            @click="$bvModal.hide('modal-camp-submit-order')"
            >取消</b-button
          >
          <b-button
            variant="outline-primary"
            :disabled="submitting"
            @click="toOrder(false)"
            >稍後付款</b-button
          >
          <b-button
            variant="primary"
            :disabled="submitting"
            @click="toOrder(true)"
            >立即結帳</b-button
          >
        </template>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import {
  apiCheckMember,
  apiPreviewCampCheckout,
  apiPostCampCheckout,
  apiGetCityTown,
} from "@/api/API.js";
import {
  GradeOptions,
  GRADE_BIRTHDAY_MISMATCH_MESSAGE,
  getGradeSummerNotice,
  validateFamilyMemberFields,
  validateEnglishNameParts,
  isGradeBirthdayMismatch as checkGradeBirthdayMismatch,
  isOtherGrade,
  CampPaymentMethodOptions,
} from "@/utils/campEnums.js";

export default {
  name: "CampCheckout",
  data() {
    return {
      checkout: {
        CartDto: [],
        MemberInfo: {
          FirstName: "",
          LastName: "",
          EnglishName: "",
          Gender: null,
          Phone: "",
          Email: "",
          City: "",
          Town: "",
          Address: "",
        },
        ProductPrice: 0,
        Discount: 0,
        TotalPrice: 0,
        AssetCouponId: null,
        AssetCouponIds: [],
        GroupApplyName: "",
        DiscountDto: {},
        AvailableCoupons: [],
        BlockedItems: [],
        RemovedAddons: [],
      },
      gradeOptions: GradeOptions,
      cityList: [],
      // 收款方式（1=現金結帳、2=轉帳）不屬於 checkout DTO，載入時不會被覆寫，送出時併入 payload；預設為轉帳。
      paymentMethod: 2,
      paymentMethodOptions: CampPaymentMethodOptions,
      submitAttempted: false,
      formErrors: {},
      couponLoading: false,
      discountsCollapsed: false,
      UploadedGroupApplyNames: [],
      previewTimer: null,
      submitting: false,
    };
  },
  computed: {
    selectedIds() {
      return this.$store.getters["campCart/selectedCartIds"];
    },
    paymentMethodText() {
      const option = this.paymentMethodOptions.find(
        (o) => o.value === this.paymentMethod,
      );
      return option ? option.text : "";
    },
    // 任一結帳商品有有效團報優惠即顯示訂單層級欄位，不再受同商品報名人數限制。
    showGroupApplySection() {
      return (this.checkout.CartDto || []).some((item) =>
        this.showGroupApplyOption(item),
      );
    },
    selectedCouponIds() {
      const ids = Array.isArray(this.checkout.AssetCouponIds)
        ? this.checkout.AssetCouponIds
        : [];
      if (ids.length) return ids;
      return this.checkout.AssetCouponId ? [this.checkout.AssetCouponId] : [];
    },
    checkoutDiscountItems() {
      const discount = this.checkout.DiscountDto || {};
      const items =
        Array.isArray(discount.AppliedDiscounts) &&
        discount.AppliedDiscounts.length
          ? discount.AppliedDiscounts
          : [discount];
      return items.filter((item) => Number(item.Discount || 0) < 0);
    },
    gradeSummerNotice() {
      return getGradeSummerNotice();
    },
    gradeBirthdayMismatchMessage() {
      return GRADE_BIRTHDAY_MISMATCH_MESSAGE;
    },
    hasRegistrantErrors() {
      return Object.keys(this.formErrors.Registrants || {}).length > 0;
    },
    // 同一位學員可能同時有主梯次與加購項目，皆帶著各自一份 Relation 副本；
    // 學員資料只顯示一張卡片（每個 RelationId 取第一筆），避免重複 DOM id 導致點一個全部展開。
    registrantItems() {
      const seen = new Set();
      return (this.checkout.CartDto || []).filter((item) => {
        if (!item.Relation || !item.RelationId) return false;
        if (seen.has(item.RelationId)) return false;
        seen.add(item.RelationId);
        return true;
      });
    },
    hasBlockedItems() {
      return (this.checkout.BlockedItems || []).length > 0;
    },
  },
  mounted() {
    apiCheckMember()
      .then(() => {
        this.init();
      })
      .catch(() => {
        alert("尚未登入");
        this._go("/");
      });
  },
  methods: {
    init() {
      if (!this.selectedIds.length) {
        this._go("/campCart");
        return;
      }

      apiGetCityTown({
        Paging: { pageSize: 9999, pageNumber: 1 },
        Search: { keywords: "city" },
      }).then((res) => {
        this.cityList = res.data || [];
      });

      // 首次進入結帳頁先不選券，由後端回傳本次可用券與原有營隊優惠試算。
      apiPreviewCampCheckout({ ids: this.selectedIds, assetCouponIds: [] })
        .then((res) => {
          this.checkout = res.data;
          this.checkout.BlockedItems = this.checkout.BlockedItems || [];
          this.checkout.RemovedAddons = this.checkout.RemovedAddons || [];
          this.syncRemovedAddonsWithStore(this.checkout.RemovedAddons);
          this.normalizeGroupApplyField();
          this.normalizeRelationDates();
          this.linkRegistrantRelations();
        })
        .catch((err) => {
          const msg = err?.response?.data?.error || "載入報名資料失敗";
          alert(msg);
          this._go("/campCart");
        });
    },
    onCityChange() {
      this.onFieldInput();
    },
    onGroupApplyInput() {
      // 一般輸入不試算；清空欄位時立即重算，避免團報優惠沿用舊結果。
      if (!String(this.checkout.GroupApplyName || "").trim()) {
        this.UploadedGroupApplyNames = [];
        this.$set(this.checkout, "DiscountDto", {});
        this.$set(this.checkout, "Discount", 0);
        this.$set(
          this.checkout,
          "TotalPrice",
          Number(this.checkout.ProductPrice || 0),
        );
        this.refreshCheckoutSummary(null, false).then(() => {
          // 後端若仍回傳舊的團報優惠，清空欄位時以前端狀態為準，避免明細被寫回。
          if (!String(this.checkout.GroupApplyName || "").trim()) {
            this.$set(this.checkout, "DiscountDto", {});
            this.$set(this.checkout, "Discount", 0);
            this.$set(
              this.checkout,
              "TotalPrice",
              Number(this.checkout.ProductPrice || 0),
            );
          }
        });
      }
    },
    toggleCoupon(assetCouponId) {
      if (!assetCouponId || this.couponLoading) return;
      const ids = this.isCouponSelected(assetCouponId)
        ? this.selectedCouponIds.filter((id) => id !== assetCouponId)
        : [...this.selectedCouponIds, assetCouponId];
      this.applyCouponSelection(ids);
    },
    isCouponSelected(assetCouponId) {
      return this.selectedCouponIds.includes(assetCouponId);
    },
    applyCouponSelection(assetCouponIds) {
      this.couponLoading = true;
      this.refreshCheckoutSummary(assetCouponIds, false)
        .catch(() => {
          this.$set(this.checkout, "AssetCouponId", null);
          this.$set(this.checkout, "AssetCouponIds", []);
        })
        .finally(() => {
          this.couponLoading = false;
        });
    },
    buildPreviewPayload(assetCouponIds) {
      const selectedAssetCouponIds = Array.isArray(assetCouponIds)
        ? assetCouponIds
        : [...this.selectedCouponIds];
      const cartDto = (this.checkout.CartDto || []).map((item) => ({
        Id: item.Id,
      }));

      return {
        ids: this.selectedIds,
        assetCouponId:
          selectedAssetCouponIds.length === 1 ? selectedAssetCouponIds[0] : null,
        assetCouponIds: selectedAssetCouponIds,
        groupApplyName: this.checkout.GroupApplyName || null,
        cartDto,
      };
    },
    refreshCheckoutSummary(assetCouponIds = null, debounce = true) {
      window.clearTimeout(this.previewTimer);
      const selectedAssetCouponIds = Array.isArray(assetCouponIds)
        ? assetCouponIds
        : [...this.selectedCouponIds];
      const run = () =>
        apiPreviewCampCheckout(this.buildPreviewPayload(selectedAssetCouponIds)).then(
          (res) => {
            const data = res.data;
            this.applyRemovedAddons(data.RemovedAddons || []);
            this.$set(this.checkout, "BlockedItems", data.BlockedItems || []);
            this.$set(this.checkout, "AssetCouponIds", data.AssetCouponIds || []);
            this.$set(
              this.checkout,
              "AssetCouponId",
              data.AssetCouponId || (data.AssetCouponIds || [])[0] || null,
            );
            this.$set(
              this.checkout,
              "AvailableCoupons",
              data.AvailableCoupons || [],
            );
            this.$set(this.checkout, "DiscountDto", data.DiscountDto || {});
            this.$set(
              this.checkout,
              "ProductPrice",
              Number(data.ProductPrice || 0),
            );
            this.$set(this.checkout, "Discount", Number(data.Discount || 0));
            this.$set(
              this.checkout,
              "TotalPrice",
              Number(data.TotalPrice || 0),
            );
          },
        );

      if (!debounce) return run();

      this.previewTimer = window.setTimeout(() => {
        run().catch(() => {});
      }, 300);

      return Promise.resolve();
    },
    // 本次 checkout 範圍內加購被剪除：只從 CartDto 移除對應項目，保留其餘項目已編輯的學員資料。
    applyRemovedAddons(removed) {
      const list = removed || [];
      if (list.length) {
        const removedIds = new Set(list.map((r) => r.CampCartId));
        this.$set(
          this.checkout,
          "CartDto",
          (this.checkout.CartDto || []).filter(
            (item) => !removedIds.has(item.Id),
          ),
        );
        this.syncRemovedAddonsWithStore(list);
        this.linkRegistrantRelations();
      }
      this.$set(this.checkout, "RemovedAddons", list);
    },
    // 後端 preview/送出已同步刪除 CampCart，這裡同步購物車 store 快取，避免下次 checkout 仍送出已刪除的 id。
    syncRemovedAddonsWithStore(removed) {
      if (!removed || !removed.length) return;
      this.$store.commit(
        "campCart/removeItems",
        removed.map((r) => r.CampCartId),
      );
    },
    // 送出訂單遇到 409（後台異動造成加購剪除或金額改變）：套用新版 preview 資料，保留使用者已輸入的學員資料。
    applyConflictPreview(data) {
      const removedIds = new Set(
        (data.RemovedAddons || []).map((r) => r.CampCartId),
      );
      const keepMap = new Map(
        (this.checkout.CartDto || []).map((item) => [item.Id, item]),
      );
      const mergedCartDto = (data.CartDto || [])
        .filter((item) => !removedIds.has(item.Id))
        .map((item) => {
          const existing = keepMap.get(item.Id);
          return existing
            ? {
                ...item,
                Relation: existing.Relation,
                SyncToLearnerProfile: existing.SyncToLearnerProfile,
              }
            : item;
        });

      this.$set(this.checkout, "CartDto", mergedCartDto);
      this.$set(this.checkout, "BlockedItems", data.BlockedItems || []);
      this.$set(this.checkout, "RemovedAddons", data.RemovedAddons || []);
      this.$set(this.checkout, "DiscountDto", data.DiscountDto || {});
      this.$set(this.checkout, "AvailableCoupons", data.AvailableCoupons || []);
      this.$set(this.checkout, "AssetCouponIds", data.AssetCouponIds || []);
      this.$set(
        this.checkout,
        "AssetCouponId",
        data.AssetCouponId != null ? data.AssetCouponId : (data.AssetCouponIds || [])[0] || null,
      );
      this.$set(this.checkout, "ProductPrice", Number(data.ProductPrice || 0));
      this.$set(this.checkout, "Discount", Number(data.Discount || 0));
      this.$set(this.checkout, "TotalPrice", Number(data.TotalPrice || 0));
      this.syncRemovedAddonsWithStore(data.RemovedAddons || []);
      this.linkRegistrantRelations();
    },
    // 同一位學員的所有 CartDto 項目（主梯次＋加購）共用同一份 Relation 物件參考，
    // 讓學員資料卡片上的編輯自動套用到該學員底下的每一筆報名快照，不必逐筆編輯。
    linkRegistrantRelations() {
      const repByRelation = {};
      (this.checkout.CartDto || []).forEach((item) => {
        if (!item.Relation || !item.RelationId) return;
        if (!repByRelation[item.RelationId]) repByRelation[item.RelationId] = item.Relation;
      });
      (this.checkout.CartDto || []).forEach((item) => {
        if (!item.Relation || !item.RelationId) return;
        const rep = repByRelation[item.RelationId];
        if (rep && item.Relation !== rep) this.$set(item, "Relation", rep);
      });
    },
    // 學員資料卡片標題副標：列出該學員本次結帳的所有梯次／加購名稱。
    registrantSessionsLabel(relationId) {
      const labels = (this.checkout.CartDto || [])
        .filter((c) => c.RelationId === relationId)
        .map((c) => c.CampProductName || c.CampProductTitle)
        .filter(Boolean);
      return [...new Set(labels)].join("、");
    },
    couponOptionText(coupon) {
      // coupon.Discount 是後端已依購物車、低消與最高折抵算好的負數；畫面取絕對值顯示正數折抵金額。
      const discount = Math.abs(Number(coupon.Discount || 0));
      const code = coupon.CouponCode ? ` (${coupon.CouponCode})` : "";
      const description = coupon.CouponDescription
        ? `，${coupon.CouponDescription}`
        : "";
      return `${coupon.CouponName ||
        "折價券"}${code}${description}，折抵 $ ${discount.toLocaleString()}`;
    },
    discountName(discount) {
      if (!discount) return "";
      return (
        discount.Title ||
        discount.CouponName ||
        discount.PromotionName ||
        discount.Description ||
        ""
      );
    },
    discountAmount(discount) {
      return Math.abs(Number(discount?.Discount || 0)).toLocaleString();
    },
    normalizeRelationDates() {
      (this.checkout.CartDto || []).forEach((item) => {
        if (!item.Relation || !item.Relation.Birthday) return;
        item.Relation.Birthday = String(item.Relation.Birthday).slice(0, 10);
      });
    },
    normalizeGroupApplyField() {
      const names = this.parseGroupApplyNames(this.checkout.GroupApplyName);
      this.$set(this.checkout, "GroupApplyName", names.join(","));
      this.UploadedGroupApplyNames = names;
    },
    parseGroupApplyNames(value) {
      if (Array.isArray(value))
        return value.map((v) => String(v || "").trim()).filter(Boolean);
      const text = String(value || "").trim();
      if (!text) return [];
      if (!text.startsWith("[")) return [text];
      try {
        const names = JSON.parse(text);
        return Array.isArray(names)
          ? names.map((v) => String(v || "").trim()).filter(Boolean)
          : [];
      } catch {
        return [text];
      }
    },
    getGroupApplyNames() {
      return String(this.checkout.GroupApplyName || "")
        .split(/[,，]/)
        .map((value) => value.trim());
    },
    isValidGroupApplyName(name) {
      const value = String(name || "").trim();
      return (
        value.length >= 2 &&
        value.length <= 50 &&
        /^[\u4e00-\u9fa5A-Za-z\s·.-]+$/.test(value) &&
        /[\u4e00-\u9fa5A-Za-z]/.test(value)
      );
    },
    validate() {
      const m = this.checkout.MemberInfo;
      const errors = {};
      if (!(m.LastName || "").trim()) errors.LastName = "請填寫姓";
      if (!(m.FirstName || "").trim()) errors.FirstName = "請填寫名";
      if (m.Gender === null || m.Gender === undefined || m.Gender === "")
        errors.Gender = "請選擇性別";
      if (!(m.Phone || "").trim()) errors.Phone = "請填寫電話";
      if (!(m.Email || "").trim()) errors.Email = "請填寫 Email";
      else if (!/^\S+@\S+\.\S+$/.test(m.Email))
        errors.Email = "Email 格式不正確";
      if (!(m.City || "").trim()) errors.City = "請選擇縣市";
      if ((m.City || "").trim() && !(m.Town || "").trim())
        errors.Town = "請填寫行政區";
      if (!(m.Address || "").trim()) errors.Address = "請填寫地址";
      const groupApplyNameError = this.getGroupApplyNameError();
      if (groupApplyNameError) errors.GroupApplyName = groupApplyNameError;

      if (this.paymentMethod !== 1 && this.paymentMethod !== 2)
        errors.PaymentMethod = "請選擇收款方式";

      // 只需驗證每位學員的代表卡片（registrantItems）；linkRegistrantRelations 已讓同一位
      // 學員底下所有 CartDto 項目共用同一份 Relation 物件，驗證一次即涵蓋全部報名快照。
      const registrantErrors = {};
      this.registrantItems.forEach((item) => {
        const r = item.Relation;
        const error =
          validateFamilyMemberFields(
            r.Name,
            r.Birthday,
            r.Gender,
            r.School,
            r.Grade,
            r.GradeMemo,
          ) || validateEnglishNameParts(r.EnglishLastName, r.EnglishFirstName, r.EnglishName);
        if (error) registrantErrors[item.RelationId] = error;
      });
      if (Object.keys(registrantErrors).length > 0)
        errors.Registrants = registrantErrors;

      return errors;
    },
    registrantError(relationId) {
      return (this.formErrors.Registrants || {})[relationId] || "";
    },
    registrantNameById(relationId) {
      const item = this.registrantItems.find((c) => c.RelationId === relationId);
      return (item && item.RelationName) || "學員";
    },
    isGradeBirthdayMismatch(grade, birthday) {
      return checkGradeBirthdayMismatch(grade, birthday);
    },
    showGradeMemo(grade) {
      return isOtherGrade(grade);
    },
    getGroupApplyNameError() {
      if (
        !this.showGroupApplySection ||
        !String(this.checkout.GroupApplyName || "").trim()
      )
        return "";
      const names = this.getGroupApplyNames();
      if (names.length === 0 || names.every((name) => !name))
        return "請至少填寫 1 位團報同行人";
      if (names.some((name) => !name)) return "請在每個逗號前後完整填寫姓名";
      if (names.some((name) => !this.isValidGroupApplyName(name)))
        return "每位姓名需為 2 至 50 字，且不可填純數字或符號";
      if (new Set(names).size !== names.length) return "團報同行人姓名不可重複";
      return "";
    },
    showGroupApplyOption(item) {
      if (!item || !item.CampProductId) return false;
      return item.CanGroupApply === true;
    },
    getFieldState(field) {
      if (!this.submitAttempted) return null;
      return this.formErrors[field] ? false : true;
    },
    getGroupFieldState() {
      if (!this.submitAttempted) return null;
      return this.formErrors.GroupApplyName ? false : true;
    },
    onFieldInput() {
      if (!this.submitAttempted) return;
      this.formErrors = this.validate();
    },
    applyGroupApplyData() {
      // 避免注音組字期間逐字呼叫 API；只有使用者主動套用且格式正確才重新試算。
      const error = this.getGroupApplyNameError();
      if (error) {
        this.$set(this.formErrors, "GroupApplyName", error);
        return;
      }
      this.$delete(this.formErrors, "GroupApplyName");
      const names = this.getGroupApplyNames().filter(Boolean);
      this.refreshCheckoutSummary(null, false).then(() => {
        this.UploadedGroupApplyNames = names;
      });
    },
    openSubmitDialog() {
      if (this.submitting || this.hasBlockedItems) return;

      this.submitAttempted = true;
      this.formErrors = this.validate();
      if (Object.keys(this.formErrors).length) return;

      this.$bvModal.show("modal-camp-submit-order");
    },
    toOrder(payNow) {
      if (this.submitting || this.hasBlockedItems) return;

      this.submitAttempted = true;
      this.formErrors = this.validate();
      if (Object.keys(this.formErrors).length) {
        this.$bvModal.hide("modal-camp-submit-order");
        return;
      }

      const cartDto = (this.checkout.CartDto || []).map((item) => {
        const cartItem = { ...item };
        delete cartItem.GroupApplyName;
        return cartItem;
      });

      const payload = {
        ...this.checkout,
        // ToOrder 以此訂單層級姓名重新驗證級距，最終保存於 CampOrder.Memo。
        GroupApplyName: this.checkout.GroupApplyName || null,
        PaymentMethod: this.paymentMethod,
        CartDto: cartDto,
        MemberInfo: {
          ...this.checkout.MemberInfo,
          Gender: Number(this.checkout.MemberInfo.Gender),
        },
      };

      this.submitting = true;
      apiPostCampCheckout(payload)
        .then((res) => {
          const data = (res && res.data) || {};
          const paymentId = data.paymentId || data.PaymentId;
          const orderId = data.orderId || data.OrderId;
          if (!payNow && orderId) {
            this._go(`/campOrderDetail?orderId=${orderId}`);
          } else if (paymentId) {
            this._go(`/campPayment/${paymentId}/report`);
          } else {
            this._go("/campRegistrationComplete");
          }
        })
        .catch((err) => {
          if (err?.response?.status === 409) {
            this.$bvModal.hide("modal-camp-submit-order");
            this.applyConflictPreview(err.response.data || {});
            alert(
              "報名資料已有異動，畫面已更新為最新金額與加購項目，請重新確認後再次送出",
            );
            return;
          }
          const msg = err?.response?.data?.error || "送出失敗，請稍後再試";
          alert(msg);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.camp-collapse-header {
  cursor: pointer;
  user-select: none;
}

.camp-chevron {
  display: inline-block;
  transition: transform 0.2s ease;
  transform: rotate(180deg);
}

.collapsed .camp-chevron {
  transform: rotate(0deg);
}

.camp-form-section-title {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.camp-group-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  vertical-align: middle;
}

.camp-group-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border: 1px solid #78b7df;
  border-radius: 4px;
  background: #eef8ff;
  color: #1d5f8a;
  font-size: 0.85rem;
  font-weight: 600;
}

.camp-coupon-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.camp-coupon-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 9px 12px;
  border: 1px solid #d8e0e8;
  border-radius: 10px;
  background: #fff;
  color: #212529;
  text-align: left;
  font-size: 0.875rem;
  line-height: 1.5;
  transition: background-color 0.15s, border-color 0.15s;
}

.camp-coupon-option:hover:not(:disabled) {
  background: #d9efff;
  border-color: #247bad;
  color: #145374;
}

.camp-coupon-option.selected {
  background: #e8f5ff;
  border-color: #3292d0;
  color: #1d5f8a;
}

.camp-coupon-option-name {
  min-width: 0;
  max-width: 12em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.camp-coupon-option-check {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #edf2f6;
  color: #64748b;
  text-align: center;
  line-height: 18px;
}

.camp-coupon-option.selected .camp-coupon-option-check {
  background: #3292d0;
  color: #fff;
}

.camp-coupon-option-amount {
  flex: 0 0 auto;
  padding-left: 8px;
  border-left: 1px dashed #c4d4e0;
  color: #b02a37;
  font-weight: 700;
  white-space: nowrap;
}

.camp-discount-list {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin: 16px -16px -16px !important;
  padding: 0 16px 16px;
  border: 1px solid #d7ece0;
  border-radius: 10px;
  background: #fff;
}

.camp-summary-panel {
  padding: 18px 16px 16px;
  border: 1px solid #d7e0e8;
  border-radius: 12px;
  background: #fff;
}

.camp-summary-panel > div:not(.camp-discount-list) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #344054;
  font-size: 1.15rem;
  line-height: 1.7;
}

.camp-summary-panel > div.text-danger {
  color: #187044 !important;
}

.camp-summary-panel > div.font-weight-bold {
  margin-top: 12px;
  padding-top: 14px;
  border-top: 1px solid #d7e0e8;
  color: #111d38;
  font-size: 1.5rem;
}

.camp-discount-heading {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-height: 58px;
  margin: 0 -16px 4px;
  padding: 10px 16px;
  border-radius: 10px 10px 0 0;
  background: #edf9f1;
  color: #176b3f;
  font-size: 1.15rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  user-select: none;
}

.camp-discount-heading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #197044;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

.camp-discount-heading-icon::after {
  content: "✓";
}

.camp-discount-heading-icon {
  font-size: 0;
  order: 0;
}

.camp-discount-count {
  order: 2;
  color: #176b3f;
  font-weight: 700;
  white-space: nowrap;
}

.camp-discount-heading::after {
  content: "⌃";
  margin-left: auto;
  font-size: 1.5rem;
  line-height: 1;
}

.camp-discount-heading.collapsed::after {
  transform: rotate(180deg);
}

.camp-discount-tag {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 70px;
  padding: 10px 14px;
  border: 1px solid #d7e0e8;
  border-radius: 10px;
  background: linear-gradient(135deg, #fbfcfd 0%, #f5f8fa 100%);
  color: #17233d;
  font-size: 0.95rem;
  font-weight: 700;
  vertical-align: middle;
}

.camp-discount-name {
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.camp-discount-amount {
  flex: 0 0 auto;
  margin-left: 10px;
  color: #176b3f;
  font-size: 1.1rem;
  font-weight: 800;
  white-space: nowrap;
}

/* 優惠明細維持結帳摘要原本的右側位置與緊湊尺寸 */
.camp-summary-panel {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.camp-summary-panel > div:not(.camp-discount-list) {
  display: block;
  line-height: inherit;
}

.camp-summary-panel > div.font-weight-bold {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
  font-size: inherit;
}

.camp-discount-list {
  align-items: flex-end;
  gap: 4px;
  margin: 4px 0 0 !important;
  padding: 0;
  border: 0;
  background: transparent;
}

.camp-discount-heading {
  min-height: 0;
  margin: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  font-size: 0.75rem;
}

.camp-discount-heading-icon {
  width: 18px;
  height: 18px;
}

.camp-discount-tag {
  width: min(100%, 330px);
  min-height: 34px;
  padding: 5px 7px 5px 10px;
  border: 1px solid #b9dec7;
  border-left: 4px solid #248653;
  border-radius: 8px;
  background: #f3fbf5;
  box-shadow: 0 2px 5px rgba(36, 134, 83, 0.08);
  font-size: 0.8125rem;
}

.camp-discount-amount {
  font-size: 0.95rem;
}

/* 參考圖版型：完整摘要卡靠右顯示 */
.camp-summary-panel {
  width: min(100%, 323px);
  margin-left: auto;
  padding: 18px 16px 16px;
  border: 1px solid #d7e0e8;
  border-radius: 12px;
  background: #fff;
}

.camp-summary-panel > div:not(.camp-discount-list) {
  display: block;
  align-items: center;
  color: #344054;
  font-size: 1rem;
  line-height: 1.7;
  white-space: nowrap;
}

.camp-summary-panel > div:not(.camp-discount-list) > strong {
  white-space: nowrap;
}

.camp-summary-panel > div.text-danger {
  color: #187044 !important;
}

.camp-summary-panel > div.font-weight-bold {
  margin-top: 12px;
  padding-top: 14px;
  border-top: 1px solid #d7e0e8;
  color: #111d38;
  font-size: 1.25rem;
}

.camp-discount-list {
  align-items: stretch;
  gap: 8px;
  margin: 16px -16px -16px !important;
  padding: 0 16px 16px;
  border: 1px solid #d7ece0;
  border-radius: 10px;
  background: #fff;
}

.camp-discount-heading {
  min-height: 46px;
  margin: 0 -16px 4px;
  padding: 10px 16px;
  border-radius: 10px 10px 0 0;
  background: #edf9f1;
  color: #176b3f;
  font-size: 0.95rem;
}

.camp-discount-heading::after {
  display: none;
}

.camp-discount-heading.collapsed::after {
  display: none;
}

.camp-discount-tag {
  width: 100%;
  min-height: 50px;
  padding: 7px 10px;
  border: 1px solid #d7e0e8;
  border-left: 4px solid #248653;
  border-radius: 10px;
  background: linear-gradient(135deg, #fbfcfd 0%, #f5f8fa 100%);
  box-shadow: 0 2px 5px rgba(36, 134, 83, 0.08);
  color: #17233d;
  font-size: 0.82rem;
}

.camp-discount-amount {
  color: #176b3f;
  font-size: 0.95rem;
  font-weight: 800;
}
</style>

